/**
 * Iubenda consent helpers.
 *
 * Iubenda manages cookie consent (and Google Consent Mode v2) at the site
 * level. For third-party tags that are NOT covered by Google Consent Mode
 * (e.g. the Facebook/Meta Pixel) we must gate their initialisation on the
 * user's consent ourselves, mirroring how the marketing website gates PostHog.
 *
 * `IubendaProvider` bridges Iubenda's consent lifecycle into a single
 * `iubenda:consent` CustomEvent whose `detail.purposes` follows the Iubenda
 * standard (e.g. `purposes[5]` = Targeting & Advertising). These helpers read
 * and subscribe to that consent in a way decoupled from the Iubenda API.
 *
 * Purpose -> Google signal mapping (validated empirically against the live
 * Iubenda config, site 3424432):
 * - purpose 4 (Measurement)             -> analytics_storage
 * - purpose 5 (Targeting & Advertising) -> ad_storage, ad_user_data, ad_personalization
 */

export const IUBENDA_PURPOSE = {
  /** Strictly necessary. Always granted. */
  STRICTLY_NECESSARY: 1,
  /** Basic interactions & functionalities. */
  BASIC_INTERACTIONS: 2,
  /** Experience enhancement. */
  EXPERIENCE: 3,
  /** Measurement / analytics -> Google `analytics_storage`. */
  MEASUREMENT: 4,
  /** Targeting & Advertising -> Google `ad_storage` / `ad_user_data` / `ad_personalization`. */
  ADVERTISING: 5,
} as const;

type Purposes = Record<number, boolean>;

type IubendaConsentDetail = {
  purposes?: Purposes;
};

/**
 * Synchronously read the current Iubenda consent for a purpose.
 * Returns `false` when Iubenda has not loaded yet or no consent was expressed.
 */
export function hasIubendaConsent(purposeId: number): boolean {
  if (typeof window === 'undefined') return false;
  const purposes = (window as unknown as { _iub?: { cs?: { consent?: { purposes?: Purposes } } } })?._iub?.cs?.consent
    ?.purposes;
  return purposes?.[purposeId] === true;
}

/**
 * Subscribe to Iubenda consent changes for a single purpose.
 *
 * Invokes `callback` with the granted state every time the user expresses or
 * updates consent (via the `iubenda:consent` bridge event), and once
 * synchronously if consent for the purpose is already stored at call time
 * (covers returning users whose preference is read on page load).
 *
 * Returns an unsubscribe function.
 */
export function onIubendaConsent(purposeId: number, callback: (granted: boolean) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const handler = (event: Event) => {
    const purposes = (event as CustomEvent<IubendaConsentDetail>).detail?.purposes || {};
    callback(purposes[purposeId] === true);
  };

  window.addEventListener('iubenda:consent', handler);

  // Fire once for already-stored consent (returning user, preference read on load).
  if (hasIubendaConsent(purposeId)) callback(true);

  return () => window.removeEventListener('iubenda:consent', handler);
}
